import { useDependencies } from '../hooks/useDependencies';
import { useEffect, useMemo, useState, useRef } from 'react';
import { CenteredCircularProgress } from '../../../common/CenteredCircularProgress';
import { useSelector } from 'react-redux';
import { selectConfigurationSourceCode } from '../../../../reducers/documentation/documentationReducer';
import { ListWithScroll } from './ListWithScroll';
import { SourceCodeHeader } from '../header/SourceCodeHeader';
import { ConfigurationType } from '../types';
import { useSweepNotifications } from '../../../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../../../reducers/notificationsReducer';
import { ConfigurationItem } from '../../../parser/ParserTypes';
import { SearchProvider } from '../search/SearchProvider';
import Stack from '@mui/material/Stack';
import { HighlightedSection } from '../search/highlight/HighlightedSection';
import { useUniversalSearchContext } from '../../universal-search/UniversalSearchContext';
import { useSearchContext } from '../search/SearchContext';
import { useSearchOnMount } from './useSearchOnMount';
import { SearchLoadingOverlay } from './SearchLoadingOverlay';

interface SourceViewProps {
  crmOrgId: string;
  configurationItem: ConfigurationItem;
  parentType: ConfigurationType;
  readOnly?: boolean;
}

const SourceView = ({
  crmOrgId,
  configurationItem,
  parentType,
  readOnly,
  containerRef,
}: SourceViewProps & { containerRef: React.RefObject<HTMLDivElement> }) => {
  const [isLoading, setIsLoading] = useState(false);

  const { getSourceCode } = useDependencies(crmOrgId);
  const { addNotification } = useSweepNotifications();
  const { searchTxt } = useUniversalSearchContext();
  const { onSearch } = useSearchContext();

  const id = useMemo(
    () => configurationItem.id ?? configurationItem.name,
    [configurationItem.id, configurationItem.name],
  ); //name for objects
  const sourceCode = useSelector(selectConfigurationSourceCode(crmOrgId, id));

  // Auto-search on mount with loading overlay
  const { isSearching } = useSearchOnMount({
    searchTerm: searchTxt,
    isDataReady: !!sourceCode,
    sectionSelector: '[data-section="sourceCode"]',
    onSearch,
  });

  useEffect(() => {
    (async () => {
      if (!sourceCode && configurationItem.filename) {
        setIsLoading(true);
        await getSourceCode(configurationItem.filename, id);
        setIsLoading(false);
      }

      if (!configurationItem.filename) {
        addNotification({
          variant: SweepNotificationVariant.Error,
          message:
            'We are unable to find this configuration file, please contact our team for details',
        });
      }
    })();
  }, [sourceCode, configurationItem.filename, id, getSourceCode, addNotification]);

  if (isLoading) {
    return <CenteredCircularProgress />;
  }

  return (
    <>
      <SourceCodeHeader
        configurationItem={configurationItem}
        type={parentType}
        crmOrgId={crmOrgId}
        readOnly={readOnly}
      />
      <Stack height="100%" width="100%" overflow="hidden" ref={containerRef} position="relative">
        <SearchLoadingOverlay isVisible={isSearching} />
        <HighlightedSection section="sourceCode" highlightEntireRow>
          <Stack data-section="sourceCode" height="100%" width="100%">
            <ListWithScroll sourceCode={sourceCode} />
          </Stack>
        </HighlightedSection>
      </Stack>
    </>
  );
};

const SourceCodeWithSearchProvider = ({
  configurationItem,
  parentType,
  readOnly,
  crmOrgId,
}: SourceViewProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <SearchProvider configurationItem={configurationItem} scrollContainerRef={containerRef}>
      <SourceView
        configurationItem={configurationItem}
        parentType={parentType}
        crmOrgId={crmOrgId}
        readOnly={readOnly}
        containerRef={containerRef}
      />
    </SearchProvider>
  );
};

export { SourceCodeWithSearchProvider as SourceView };
